import { Fragment, ReactNode, useState } from "react";

import { Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { LazyImg } from "@/components/helpers/ui/LazyImg";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { Carousel } from "@/components/pieces/carousel";
import { Testimonial } from "@/types/marketing";

interface QuotesProps {
  quotes: Testimonial[];
  blobs: string[];
  reversed?: boolean;
  headline?: string;
  imageWidth?: string;
  imageHeight?: string;
  backgroundWidth?: string;
  showImages?: boolean;
  header?: ReactNode;
  style?: React.CSSProperties;
  options?: Record<string, unknown>;
}

export function Quotes({
  quotes,
  blobs,
  reversed,
  headline = "Upgrade your life at work",
  imageWidth = "260px",
  imageHeight = "254.41px",
  backgroundWidth = "100%",
  showImages = true,
  header,
  style = {},
  options = {},
}: QuotesProps) {
  const [activeItem, setActiveItem] = useState(0);

  const grids = [
    <Box key="carousel" width={[1, 2 / 3]} mb={3} mt={24}>
      <Carousel
        options={{
          loop: true,
          align: "center",
          containScroll: "trimSnaps",
          dragFree: false,
          ...options,
        }}
        onInit={() => {}}
        onBeforeChange={() => {}}
        onAfterChange={(index) => setActiveItem(index)}
      >
        {quotes.map((item) => {
          return (
            <Box
              key={item.name}
              style={{ fontSize: 20, lineHeight: 1.5 }}
              px={3}
              pb={1}
            >
              <Box>"{item.quote}"</Box>
              <Box alignItems="center" mt={3}>
                <b>{item.name}</b>
              </Box>
              {item.specialize && (
                <Box alignItems="center" mt={2}>
                  {item.specialize}
                </Box>
              )}
            </Box>
          );
        })}
      </Carousel>
    </Box>,
    showImages ? (
      <Flex
        key="photo"
        width={[1, 1 / 3]}
        my={3}
        style={{
          textAlign: "center",
          position: "relative",
        }}
        height={[310]}
        justifyContent="center"
        alignSelf="flex-start"
      >
        {quotes.map((quote, index) => {
          return (
            <QuoteImage
              key={index}
              className={activeItem === index ? "active" : undefined}
            >
              <Box
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 0,
                }}
              >
                <EmbedSvg
                  src={blobs[index]}
                  width={backgroundWidth}
                  height="auto"
                />
              </Box>
              <Media at="xs">
                <Box p={4}>
                  <LazyImg
                    src={quote.photo}
                    width="100%"
                    height="100%"
                    style={{
                      position: "relative",
                      borderRadius: 10,
                      overflow: "hidden",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Box>
              </Media>
              <Media greaterThan="xs">
                <LazyImg
                  src={quote.photo}
                  style={{
                    position: "relative",
                    borderRadius: 10,
                    overflow: "hidden",
                    margin: "29px 60px 0 37px",
                    width: imageWidth,
                    height: imageHeight,
                  }}
                />
              </Media>
            </QuoteImage>
          );
        })}
      </Flex>
    ) : null,
  ];

  return (
    <Box style={{ backgroundColor: Colors.WHITE, ...style }} pt={60} pb={4}>
      <div className="container" style={{ padding: "0 18px" }}>
        {header ? (
          header
        ) : (
          <Box className="text-center" pb={2}>
            <h2 style={{ color: "#2D2750", fontSize: 40 }}>{headline}</h2>
          </Box>
        )}
        <Flex
          flexWrap="wrap"
          alignItems="center"
          justifyContent="center"
          my={[2, 4]}
        >
          {(reversed ? grids.reverse() : grids).map((item, index) => (
            <Fragment key={index}>{item}</Fragment>
          ))}
        </Flex>
      </div>
    </Box>
  );
}

const QuoteImage = styled("div")`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  transition:
    opacity,
    transform 250ms ease;
  opacity: 0;
  transform: scale(0);

  &.active {
    position: relative;
    left: auto;
    top: auto;
    transform: scale(1);
    opacity: 1;
  }
`;
